import React, { useState, useEffect } from 'react';
import { X, Shield, Globe, Lock, Users, Trash2 } from 'lucide-react';
import * as clusterService from '../services/clusterService';
import UserSearchInput from './UserSearchInput';
import PlaygroundConfigEditor from './PlaygroundConfigEditor';

const ALL_PERMISSIONS = ['view', 'ssh', 'stop', 'start', 'teardown', 'cost'];

const ClusterSharingModal = ({ cluster, curatedImage, onClose, onUpdated, onPlaygroundSaved }) => {
  const [visibility, setVisibility] = useState(cluster.visibility || 'private');
  const [publicPermissions, setPublicPermissions] = useState(() => {
    try {
      return JSON.parse(cluster.public_permissions || '["view"]');
    } catch { return ['view']; }
  });
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visibility === 'semi_private') fetchGrants();
  }, [visibility]);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      const data = await clusterService.getClusterGrants(cluster.id);
      if (data.success) setGrants(data.grants || []);
    } catch (err) { console.error('Failed to fetch grants:', err); }
    finally { setLoading(false); }
  };

  const handleSaveVisibility = async () => {
    setSaving(true); setError(null);
    try {
      await clusterService.setClusterVisibility(cluster.id, visibility, publicPermissions);
      if (onUpdated) onUpdated();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleAddUser = async (user) => {
    try {
      const data = await clusterService.addClusterGrant(cluster.id, user.id, ['view']);
      if (data.success) setGrants(data.grants || []);
    } catch (err) { setError(err.message); }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await clusterService.removeClusterGrant(cluster.id, userId);
      setGrants(grants.filter(g => g.user_id !== userId));
    } catch (err) { setError(err.message); }
  };

  const handleUpdateGrantPermissions = async (userId, permissions) => {
    try {
      await clusterService.addClusterGrant(cluster.id, userId, permissions);
      setGrants(grants.map(g => g.user_id === userId ? { ...g, permissions: JSON.stringify(permissions) } : g));
    } catch (err) { setError(err.message); }
  };

  const togglePublicPermission = (perm) => {
    setPublicPermissions(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const toggleGrantPermission = (grant, perm) => {
    let current;
    try { current = JSON.parse(grant.permissions || '["view"]'); } catch { current = ['view']; }
    const updated = current.includes(perm) ? current.filter(p => p !== perm) : [...current, perm];
    if (!updated.includes('view')) updated.unshift('view');
    handleUpdateGrantPermissions(grant.user_id, updated);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3 text-white">
            <Shield size={20} />
            <h2 className="text-lg font-bold">Settings — {cluster.cluster_name}</h2>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Visibility</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'private', label: 'Private', icon: Lock, desc: 'Only you can see this' },
                { value: 'public', label: 'Public', icon: Globe, desc: 'All users can see this' },
                { value: 'semi_private', label: 'Shared', icon: Users, desc: 'Selected users only' },
              ].map(({ value, label, icon: Icon, desc }) => (
                <button key={value} onClick={() => setVisibility(value)}
                  className={`p-3 rounded-lg border-2 text-left transition-colors ${visibility === value ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <Icon size={18} className={visibility === value ? 'text-purple-600' : 'text-gray-400'} />
                  <div className="text-sm font-medium mt-1">{label}</div>
                  <div className="text-xs text-gray-500">{desc}</div>
                </button>
              ))}
            </div>
          </div>

          {visibility === 'public' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Public Permissions</label>
              <div className="flex flex-wrap gap-2">
                {ALL_PERMISSIONS.map(perm => (
                  <button key={perm} onClick={() => togglePublicPermission(perm)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${publicPermissions.includes(perm) ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                    {perm}
                  </button>
                ))}
              </div>
            </div>
          )}

          {visibility === 'semi_private' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Add Users</label>
              <UserSearchInput onSelectUser={handleAddUser} />
              {loading ? (
                <div className="text-center py-4 text-gray-500 text-sm">Loading grants...</div>
              ) : grants.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {grants.map(grant => {
                    let perms;
                    try { perms = JSON.parse(grant.permissions || '["view"]'); } catch { perms = ['view']; }
                    return (
                      <div key={grant.user_id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="text-sm font-medium">{grant.first_name || grant.username || 'User'} {grant.last_name || ''}</span>
                            <span className="text-xs text-gray-500 ml-2">{grant.email}</span>
                          </div>
                          <button onClick={() => handleRemoveUser(grant.user_id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {ALL_PERMISSIONS.map(perm => (
                            <button key={perm} onClick={() => toggleGrantPermission(grant, perm)} disabled={perm === 'view'}
                              className={`px-2 py-0.5 rounded text-xs font-medium border transition-colors ${perms.includes(perm) ? 'bg-purple-100 text-purple-800 border-purple-300' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'} ${perm === 'view' ? 'opacity-70 cursor-default' : ''}`}>
                              {perm}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-4 text-center py-4 text-gray-500 text-sm">No users granted access yet.</div>
              )}
            </div>
          )}

          {/* Playground Configuration — shared editor, also available standalone
              on the Playground Config page (no cluster/NATS worker required). */}
          {curatedImage && (
            <div className="border-t border-gray-200 pt-4">
              <PlaygroundConfigEditor image={curatedImage} onSaved={onPlaygroundSaved} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
          <button onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm">
            Cancel
          </button>
          <button onClick={handleSaveVisibility} disabled={saving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClusterSharingModal;
